// TKOS Lead-Generation Wizard — shared types.
//
// The wizard is an iframe-embedded 4-step tool (Build Room → Style & Colour →
// Your Info → Review) shown on retailer websites. It collects consumer kitchen
// requirements and posts them to `/api/public/wizard-submit`, which creates
// the customer + lead instantly and returns a signed magic-link URL into the
// customer portal.
//
// All state is held in a single immutable-ish `WizardState` object that lives
// in `wizard/index.ts`. Each step mounts into the shell, mutates its slice via
// `update*` helpers, then advances. State is NOT persisted to localStorage
// (that was the broken legacy path) — it lives only for the session.

/** Retailer branding pulled from `/api/wizard/branding/:retailerId`. */
export interface RetailerBranding {
  retailerId: string;
  primary: string;        // hex, e.g. "#f97316"
  secondary: string;      // hex
  text: string;           // hex (button text colour)
  logoUrl: string;        // may be ""
  fontFamily: string;     // e.g. "Inter"
  buttonStyle: "rounded" | "square" | "pill";
  welcomeHeading: string;
  welcomeSubtext: string;
  showPoweredBy: boolean;
  /** Derived radius from buttonStyle, supplied by API. */
  radius: string;
  /** True if the retailer hasn't customised yet (we still render). */
  isDefault: boolean;
  /** Retailer phone for the "or call us" fallback — backend may include later. */
  phone?: string;
  businessName?: string;
  /** Global customer-render feature flag (from /api/wizard/branding). When
   *  false, the splash drops all render / "instant kitchen design" language. */
  customerRenderEnabled?: boolean;
  /** Which appointment types this retailer offers in the booking step
   *  (migration 0072). Absent → the wizard shows all of them. */
  appointmentTypes?: AppointmentTypesConfig;

  // ── Retailer-editable wizard copy (2026-08-10 redesign) ────────────────
  // Marketing / narrative strings only. Form field labels are deliberately
  // NOT configurable — see wizard_branding.js DEFAULTS.
  splashHeadline: string;
  splashCtaLabel: string;
  reviewHeadline: string;
  reviewSubhead: string;
  whatHappensNext: string;
  /** 'gallery' → hero photography comes from the Style & Colour door ranges;
   *  'custom' → use heroImages. */
  heroImageSource: HeroImageSource;
  /** In 'gallery' mode: the retailer's chosen subset (empty = all ranges).
   *  In 'custom' mode: their own uploads. */
  heroImages: string[];
}

export type HeroImageSource = "gallery" | "custom";

// ── Step 1: Build Room ────────────────────────────────────────────────────────

/** A wall is a directed segment between two points (mm-space). */
export interface Wall {
  id: string;
  x1: number;   // mm
  y1: number;   // mm
  x2: number;   // mm
  y2: number;   // mm
}

/** A door/window/boiler marker is placed at a position along a wall. */
export interface RoomMarker {
  id: string;
  kind: "door" | "window" | "boiler";
  wallId: string;        // which wall it sits on
  /** Position along the wall, 0..1. */
  t: number;
  /** Width of the opening in mm (doors ~ 800, windows variable). */
  widthMm: number;
}

/** Kitchen layout shape — drives the AI Render camera-angle count. */
export type LayoutType = "single_wall" | "galley" | "l_shape" | "u_shape";

export interface RoomDimensions {
  walls: Wall[];
  doors: RoomMarker[];     // markers of kind "door"
  windows: RoomMarker[];   // markers of kind "window"
  boilers: RoomMarker[];   // markers of kind "boiler"
  ceilingHeightMm: number; // typical 2400
  /** Kitchen shape (single wall / galley / L / U). Drives render camera angles. */
  layoutType?: LayoutType;
  /** Whether the customer wants a kitchen island. Adds an island hero render. */
  hasIsland?: boolean;
  /** Free-text "Kitchen Notes" — surfaced to the retailer in the CRM lead notes blob. */
  notes: string;
}

// ── Step 2: Style & Colour ────────────────────────────────────────────────────

/** Door-range row as returned by `/api/door-ranges`. We only consume the fields
 *  we render; the backend persists much more, but the wizard doesn't need it. */
export interface DoorRange {
  id: string;
  supplierId?: string;
  name: string;
  sku?: string;
  description?: string;
  productCategory?: string;
  category?: string;
  subCategories?: string[];
  styleTags?: string[];
  finishType?: string;
  priceGroup?: string;             // "£" .. "££££"
  material?: string;
  colours?: Array<{ name: string; hex?: string; availability?: string; frontalImage?: string }>;
  paintToOrder?: { enabled: boolean; count: number; label: string; description: string };
  portfolioImages?: Array<{ url: string; colourName?: string }>;
  heroImage?: string;
  heroImageColour?: string;
  doorCloseUps?: Array<{ url: string; colourName?: string }>;
  doorCloseUp?: string;
  status?: string;
}

export type WorktopChoice =
  | "Laminate" | "Solid Wood" | "Quartz" | "Granite" | "Composite" | "Dekton" | "Porcelain" | "";

export type StorageChoice = "Bin" | "Corner Solutions" | "Larder" | "Internal Drawers";

export interface AppliancesSelection {
  // All appliance groups are multi-select (a customer can want, e.g., a
  // standard oven AND a warming drawer AND a steam oven). Each holds the
  // selected option labels.
  sink: string[];        // "Sink & Taps": Single | 1.5 Bowl | Double | Boiling tap | Filtered water tap
  fridge: string[];      // "Refrigeration": Larder | 70-30 | 60-40 | 50-50 | American | Wine cooler
  hob: string[];         // Gas | Electric | Induction | Downdraught | 60cm | 75cm | 90cm
  oven: string[];        // Single | Double | Built Under | Built In | Warming drawer | Steam oven
  extractor: string[];   // Chimney | Canopy | Downdraught
  microwave: string[];   // Standard | Combi
  dishwasher: string[];  // Standard 60cm | Slimline 45cm | Fully integrated | Semi integrated
  washing: string[];     // Washing machine | Tumble dryer | Washer dryer
  lighting: string[];    // any of "Under Cabinet" | "Plinth" | "In-Cabinet"
}

export interface StyleChoices {
  doorRangeId: string;
  doorRange: string;       // human-readable name (also persisted for the payload)
  colourName: string;      // selected swatch from the range
  worktop: WorktopChoice;
  storage: StorageChoice[];
  appliances: AppliancesSelection;
  // Material enums (visual form grids + Aria conversation both write these;
  // the render prompt engine reads them via its descriptor maps).
  worktopMaterial?: string;
  /** Handle SHAPE only, since 2026-08-20: "bar" | "cup" | "knob" |
   *  "handleless" | "". The finish moved to `handleFinish`.
   *
   *  NOTE this is the wizard's INTERNAL shape. It is NOT what goes on the
   *  wire — `submit.ts` recombines style+finish into the legacy composite
   *  token (brass_bar / chrome_bar / black_bar / nickel_bar) that the render
   *  engine's descriptor maps are keyed on. Never send this value raw. */
  handleStyle?: string;
  /** Handle FINISH: "brass" | "steel" | "black" | "chrome" | "". Only
   *  meaningful for the shapes in HANDLE_FINISHABLE; handleless and
   *  "not sure yet" carry none. "steel" is the friendlier name for what the
   *  render engine still calls nickel — same finish, not a new one. */
  handleFinish?: string;
  flooringType?: string;
  doorStyle?: string;
}

// ── Step 3: Your Info ─────────────────────────────────────────────────────────

export type BudgetChoice =
  | "Under £5k" | "£5k-£10k" | "£10k-£15k" | "£15k-£20k" | "£20k+" | "";

export type TimelineChoice =
  | "ASAP" | "1-3 months" | "3-6 months" | "6-12 months" | "Just browsing" | "";

export type LeadSourceChoice =
  | "Google" | "Social media" | "Word of mouth" | "Existing customer" | "Supplier recommendation" | "Other" | "";

export type BookingType =
  // Offered as of 2026-08-12: showroom | home-visit | callback.
  // "remote" and "supply-only" are retained for backward-compat — historical
  // leads still carry them and must still render their real label — but the
  // wizard no longer offers either (see APPOINTMENT_TYPES in step-3-info.ts).
  | "callback" | "showroom" | "home-visit" | "remote" | "supply-only" | "";

/** The appointment types the lead-gen wizard offers, keyed by BookingType id.
 *  Per-retailer (migration 0072); a key absent or true → that type is shown. */
export type AppointmentTypesConfig = Partial<Record<Exclude<BookingType, "">, boolean>>;

export interface CustomerInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  /** Street address for the customer's property. */
  address: string;
  /** UK postcode (uppercase, space-separated). Optional but persisted into the CRM contact for geocoding + delivery planning. */
  postcode: string;
}

export interface PhotoUpload {
  name: string;
  /** data URL (base64). The submit endpoint accepts up to ~5 MB each. */
  dataUrl: string;
  sizeBytes: number;
}

export interface BookingChoice {
  bookingType: BookingType;
  preferredDate: string;   // "YYYY-MM-DD"
  preferredTime: string;   // "HH:MM" (24h)
}

export interface ProjectInfo {
  budget: BudgetChoice;
  timeline: TimelineChoice;
  leadSource: LeadSourceChoice;
  photos: PhotoUpload[];
  /** Floor-plan / photo files the customer uploaded on step 1 instead of (or as
   *  well as) drawing the room. Any format accepted; renamed on submit to
   *  `[CustomerName]_plan` / `[CustomerName]_photo`. */
  planUploads: PhotoUpload[];
  /** True when the customer chose "I can't measure my room — arrange a home
   *  design visit" on step 1. Flags the lead so the retailer follows up. */
  step1Skipped: boolean;
  gdprConsent: boolean;
}

// ── Combined wizard state ─────────────────────────────────────────────────────

export interface WizardState {
  retailerId: string;
  branding: RetailerBranding;
  room: RoomDimensions;
  style: StyleChoices;
  customer: CustomerInfo;
  project: ProjectInfo;
  booking: BookingChoice;
  /** Index of the current step (0..3). */
  stepIndex: number;
  /** True once step 1 captured a name + email and the server accepted them.
   *  Drives step 3's pre-fill and the Logout control in the bottom nav.
   *  NOTE: this is not a portal session and grants no account access — the
   *  30-day magic link is still only issued at submit. See wizard-start. */
  accountCaptured: boolean;
}

// ── Submission shape (matches `POST /api/public/wizard-submit` contract) ──────

export interface Submission {
  retailerId: string;
  customer: CustomerInfo;
  project: {
    roomDimensions: {
      walls: Wall[];
      doors: RoomMarker[];
      windows: RoomMarker[];
      /** Boiler markers — placed on the canvas so the designer can see them. */
      boilers: RoomMarker[];
      ceilingHeightMm: number;
    };
    /** Kitchen shape + island — feed the AI Render camera-angle engine. */
    layout_type?: LayoutType;
    has_island?: boolean;
    /** Material enums — render prompt engine reads these via descriptor maps. */
    worktop_material?: string;
    /** LEGACY COMPOSITE token (brass_bar / chrome_bar / black_bar / nickel_bar
     *  / cup / knob / handleless), produced by `legacyHandleToken()`. Not the
     *  wizard's internal `handleStyle` — see the note there. */
    handle_style?: string;
    /** Handle finish on its own (brass | steel | black | chrome). Additive as
     *  of 2026-08-20; no server consumer yet. */
    handle_finish?: string;
    flooring_type?: string;
    door_style?: string;
    styleChoices: {
      /** Supplier door-range catalogue id — preferred join key for the CRM brief. */
      doorRangeId: string;
      /** Human-readable door-range name (kept for reading the lead at a glance). */
      doorRange: string;
      /** Swatch chosen from the selected door range. */
      colourName: string;
      worktop: string;
      /** Storage preferences chosen during the wizard (Bin / Larder / Corner / Drawers). */
      storage: StorageChoice[];
      appliances: AppliancesSelection;
    };
    /** Free-text customer-entered notes from the step-1 "Kitchen Notes" textarea. */
    notes: string;
    budget: string;
    timeline: string;
    leadSource: string;
    photos: PhotoUpload[];
    /** Customer-uploaded floor plan / photo files (already renamed). Persisted
     *  server-side as shared design files (→ CRM docs + customer portal). */
    planUploads: PhotoUpload[];
    /** True when the customer skipped drawing their room on step 1. */
    step1Skipped: boolean;
    /** Self-contained SVG of the drawn floor plan, persisted server-side as a
     *  shared design file (→ CRM docs + customer portal). "" when nothing drawn. */
    floorPlanSvg?: string;
  };
  booking: {
    bookingType: string;
    preferredDate: string;
    preferredTime: string;
  };
  /** Honeypot — always empty for legitimate traffic. */
  website: string;
  /** Cloudflare Turnstile token from step 4's widget, verified server-side. */
  turnstileToken: string;
}

export interface SubmissionSuccess {
  ok: true;
  customerId: string;
  leadId: string;
  portalToken: string;
  portalUrl: string;
}

export interface SubmissionError {
  ok: false;
  error: string;
}

// ── Factory: a freshly-initialised state, before retailer branding loads. ─────

export function emptyState(retailerId: string): WizardState {
  return {
    retailerId,
    branding: {
      retailerId,
      primary: "#f97316",
      secondary: "#1a1a1c",
      text: "#ffffff",
      logoUrl: "",
      fontFamily: "Inter",
      buttonStyle: "rounded",
      welcomeHeading: "Design your dream kitchen in 90 seconds",
      welcomeSubtext: "Answer a few quick questions and we'll get back to you.",
      showPoweredBy: true,
      radius: "8px",
      isDefault: true,
      splashHeadline: "Let's plan your kitchen",
      splashCtaLabel: "Get started",
      reviewHeadline: "Thank you — that's everything we need.",
      reviewSubhead:
        "Have a check through what you've told us, then submit and your specialist will be in touch within one working day.",
      whatHappensNext:
        "We'll create your personal kitchen portal so you can chat with your designer, view photos and approve quotes — all in one place.",
      heroImageSource: "gallery",
      heroImages: [],
    },
    room: { walls: [], doors: [], windows: [], boilers: [], ceilingHeightMm: 2400, notes: "" },
    style: {
      doorRangeId: "",
      doorRange: "",
      colourName: "",
      worktop: "",
      storage: [],
      appliances: {
        sink: [],
        fridge: [],
        hob: [],
        oven: [],
        extractor: [],
        microwave: [],
        dishwasher: [],
        washing: [],
        lighting: [],
      },
    },
    customer: { firstName: "", lastName: "", email: "", phone: "", address: "", postcode: "" },
    project: {
      budget: "",
      timeline: "",
      leadSource: "",
      photos: [],
      planUploads: [],
      step1Skipped: false,
      gdprConsent: false,
    },
    booking: { bookingType: "", preferredDate: "", preferredTime: "" },
    stepIndex: 0,
    accountCaptured: false,
  };
}
